import type { PlpEditorialLink } from "@/data/plp-editorial-links";

/**
 * Keeps the editorial block from repeating a link the page copy already carries.
 * Pure helpers; `PlpSeoBlock` decides what "the copy" is.
 */

const MARKDOWN_HREF_RE = /\]\(([^)]+)\)/g;
const HTML_HREF_RE = /\bhref\s*=\s*["']([^"']+)["']/gi;
const ABSOLUTE_HREF_RE = /https?:\/\/[^\s)\]"'<>]+/gi;

const stripOrigin = (href: string) => href.replace(/^https?:\/\/[^/]+/i, "");

/** Strip origin, query string and trailing slash so `/blog/x` matches an absolute URL. */
export const normalizeEditorialHref = (href: string): string => {
  let path = stripOrigin(href.trim());
  const queryIndex = path.indexOf("?");

  if (queryIndex !== -1) {
    path = path.slice(0, queryIndex);
  }

  if (path && !path.startsWith("/")) {
    path = `/${path}`;
  }

  const hashIndex = path.indexOf("#");
  const hash = hashIndex === -1 ? "" : path.slice(hashIndex);
  let pathname = hashIndex === -1 ? path : path.slice(0, hashIndex);

  if (pathname.length > 1 && pathname.endsWith("/")) {
    pathname = pathname.slice(0, -1);
  }

  return `${pathname}${hash}`;
};

const collectDescriptionHrefs = (markdown: string): Set<string> => {
  const hrefs = new Set<string>();

  const take = (raw: string | undefined) => {
    if (!raw) {
      return;
    }

    const token = raw.trim().split(/\s+/)[0];

    if (!token) {
      return;
    }

    hrefs.add(normalizeEditorialHref(token.replace(/^<|>$/g, "")));
  };

  for (const match of markdown.matchAll(MARKDOWN_HREF_RE)) {
    take(match[1]);
  }

  for (const match of markdown.matchAll(HTML_HREF_RE)) {
    take(match[1]);
  }

  for (const match of markdown.matchAll(ABSOLUTE_HREF_RE)) {
    take(match[0]);
  }

  return hrefs;
};

/** Drop editorial links whose path already appears in the listing-page description. */
export const filterLinksAgainstDescription = (
  links: PlpEditorialLink[],
  markdown?: string
): PlpEditorialLink[] => {
  if (!markdown?.trim()) {
    return links;
  }

  const described = collectDescriptionHrefs(markdown);

  return links.filter(
    link => !described.has(normalizeEditorialHref(link.href))
  );
};
